/**
 * FACE — the four-segment switch between financial sections, with the cycle named at its centre.
 *
 * Cash, Financing, Assets and Earnings interlock around a centre disc. Each segment carries a block
 * arrow into the next one, and the arrows are the point: they draw the flow Cash → Financing →
 * Assets → Earnings → Cash, so the four shapes are one shape rotated by 90° each time. That is also
 * how they are built — `CASH` below is the only outline written by hand.
 *
 * **Buttons, not an image.** Every segment is a real `<button>` covering the whole square and cut to
 * its outline with a `clip-path`. The browser does not hit-test outside a clip-path, so a click in
 * one segment's notch lands on the neighbour whose arrow fills it, and the shapes can overlap
 * without stealing each other's clicks.
 *
 * **Content swaps in place, so this is a tablist** (`role="tab"`, `aria-selected`, one tab stop).
 * Arrow keys move spatially around the 2×2, Home and End go to the start and end of the flow, and
 * selection follows focus — switching is cheap, so there is nothing to confirm. Where a page has
 * the content it swaps, pass `panelId` and give that panel `aria-labelledby={faceTabId(id, value)}`;
 * the tabs only claim `aria-controls` when there is a panel to point at.
 *
 * **It reports only what it is given.** A segment with no status shows a hollow dot and "Not
 * rated", no trend draws no line, and no score leaves the centre's rule as a plain rule rather than
 * a meter. Nothing here invents a value; see `layout/MainLayout/RightBar/FacePanel.tsx` for what
 * is still undecided about where they come from.
 */

import type { KeyboardEvent, ReactNode } from 'react';
import { useRef } from 'react';

import Box from '@mui/material/Box';
import SvgIcon from '@mui/material/SvgIcon';
import type { Theme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

export type FaceKey = 'cash' | 'financing' | 'assets' | 'earnings';

/**
 * Tone decides the dot's colour; `label` is the line under the trend ("Cash conversion strong").
 * Kept apart because the wording is the financial team's call, not this component's.
 */
export type FaceTone = 'positive' | 'neutral' | 'negative';

export interface FaceReading {
  status?: { tone: FaceTone; label: string };
  /** Oldest first. Two or more points draw a line; fewer draw nothing. */
  trend?: number[];
}

export interface FaceHealth {
  score: number;
  outOf?: number;
}

export const faceTabId = (id: string, key: FaceKey) => `${id}-tab-${key}`;
export const facePanelId = (id: string) => `${id}-panel`;

// ------------------------------------------------------------------------------------------------
// Geometry — one 100×100 square, 2.5 units of gap, the centre disc r=15 at (50,50).
// ------------------------------------------------------------------------------------------------

type Seg = ['M' | 'L', number, number] | ['A', number, 0 | 1, number, number] | ['Z'];

/**
 * Cash, the top-left segment, traced clockwise.
 *
 * - Its right edge (x=48.75) sends a block arrow into Financing: a stem 6 high crossing the gap,
 *   then a 45° head from x=52 to its tip at (58.5,16).
 * - Its bottom edge has the notch that takes Earnings' arrow: the head pushed out by the gap, so the
 *   gap stays 2.5 along the slopes. Only the head needs a notch — the stem is inside the gap.
 * - The concave arc is concentric with the disc (r = 15 + gap), with r=3 fillets where it meets the
 *   straight edges; the fillet points are solved for those two radii, not eyeballed.
 */
const CASH: Seg[] = [
  ['M', 12, 0],
  ['L', 44.75, 0],
  ['A', 4, 1, 48.75, 4],
  ['L', 48.75, 13],
  ['L', 52, 13],
  ['L', 52, 9.5],
  ['L', 58.5, 16],
  ['L', 52, 22.5],
  ['L', 52, 19],
  ['L', 48.75, 19],
  ['L', 48.75, 29.945],
  ['A', 3, 1, 46.372, 32.88],
  ['A', 17.5, 0, 32.88, 46.372],
  ['A', 3, 1, 29.945, 48.75],
  ['L', 26.786, 48.75],
  ['L', 16, 37.964],
  ['L', 5.214, 48.75],
  ['L', 4, 48.75],
  ['A', 4, 1, 0, 44.75],
  ['L', 0, 12],
  ['A', 12, 1, 12, 0],
  ['Z']
];

/** Quarter turns clockwise about the centre. Rotation keeps orientation, so arc flags carry over. */
function outline(quarterTurns: number) {
  const turn = (x: number, y: number): [number, number] => {
    for (let i = 0; i < quarterTurns; i++) [x, y] = [100 - y, x];
    return [x, y];
  };
  return CASH.map((seg) => {
    if (seg[0] === 'Z') return 'Z';
    if (seg[0] === 'A') {
      const [, r, sweep, x, y] = seg;
      return `A${r} ${r} 0 0 ${sweep} ${turn(x, y).join(' ')}`;
    }
    const [cmd, x, y] = seg;
    return `${cmd}${turn(x, y).join(' ')}`;
  }).join(' ');
}

// ------------------------------------------------------------------------------------------------
// The four segments, in flow order — which is also tab order.
// ------------------------------------------------------------------------------------------------

/** A stack of coins. Material has no such icon, and the mock-up's Cash is a coin stack, not a piggy bank. */
function CoinsIcon() {
  const band = (y: number) => `M4 ${y}v2.2a8 2.6 0 0 0 16 0V${y}a8 2.6 0 0 1-16 0z`;
  return (
    <SvgIcon fontSize="inherit">
      <ellipse cx="12" cy="6" rx="8" ry="2.6" />
      <path d={[6.9, 10, 13.1, 16.2].map(band).join('')} />
    </SvgIcon>
  );
}

interface SegmentDef {
  key: FaceKey;
  label: string;
  icon: ReactNode;
  turns: number;
  /** Row and column in the 2×2, for arrow-key movement. */
  cell: [number, number];
  /** Where the content sits: in the segment's outer corner, clear of the disc and the notches. */
  place: { top?: string; bottom?: string; left?: string; right?: string };
  /** How much primary is mixed into the paper, gradient start → end, per mode. Earnings sits deepest, as in the mock-up. */
  tint: { light: [number, number]; dark: [number, number] };
}

const SEGMENTS: SegmentDef[] = [
  {
    key: 'cash',
    label: 'Cash',
    icon: <CoinsIcon />,
    turns: 0,
    cell: [0, 0],
    place: { top: '6%', left: '11%' },
    tint: { light: [9, 16], dark: [24, 18] }
  },
  {
    key: 'financing',
    label: 'Financing',
    icon: <AccountBalanceIcon fontSize="inherit" />,
    turns: 1,
    cell: [0, 1],
    place: { top: '6%', right: '6%' },
    tint: { light: [7, 13], dark: [20, 15] }
  },
  {
    key: 'assets',
    label: 'Assets',
    icon: <BarChartIcon fontSize="inherit" />,
    turns: 2,
    cell: [1, 1],
    place: { bottom: '6%', right: '6%' },
    tint: { light: [11, 19], dark: [27, 21] }
  },
  {
    key: 'earnings',
    label: 'Earnings',
    icon: <TrendingUpIcon fontSize="inherit" />,
    turns: 3,
    cell: [1, 0],
    place: { bottom: '6%', left: '11%' },
    tint: { light: [17, 27], dark: [34, 27] }
  }
];

const TONE_COLOR: Record<FaceTone, string> = {
  positive: 'success.main',
  neutral: 'grey.500',
  negative: 'error.main'
};

// Colours come from the theme's brand ramps, so dark mode and any palette change carry through:
// segments are tints of primary (the logo blue), the selected one takes the secondary highlight
// (the logo orange) — as a tint in light mode; in dark mode orange mixed into navy turns brown, so
// there it is a brighter blue with the orange carried by the ring and icon — and the centre is solid primary. One ink per segment — label, trend and
// status all speak in it, as in the mock-up.
const mix = (color: string, percent: number, base: string) => `color-mix(in srgb, ${color} ${percent}%, ${base})`;
const gradient = (from: string, to: string) => `linear-gradient(150deg, ${from} 0%, ${to} 100%)`;

// ------------------------------------------------------------------------------------------------

export function FaceSwitch({
  id,
  value,
  onChange,
  readings = {},
  health,
  title = 'FACE',
  subtitle = 'Financial Cycle',
  healthLabel = 'Financial health',
  panelId,
  'aria-label': ariaLabel = 'Financial sections'
}: {
  /** Base for the tab, panel and clip-path ids; must be unique on the page. */
  id: string;
  value: FaceKey;
  onChange: (key: FaceKey) => void;
  readings?: Partial<Record<FaceKey, FaceReading>>;
  health?: FaceHealth;
  title?: string;
  subtitle?: string;
  /** The meter's accessible name, and the score's visible caption. */
  healthLabel?: string;
  /** The id of the panel this switch swaps, when the page has one. */
  panelId?: string;
  'aria-label'?: string;
}) {
  const tabs = useRef<Partial<Record<FaceKey, HTMLButtonElement | null>>>({});
  // url(#…) is parsed as CSS, so the clip ids keep only characters that need no escaping.
  const clipBase = `face-clip-${id.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const select = (key: FaceKey) => {
    onChange(key);
    tabs.current[key]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent, from: SegmentDef) => {
    const [row, col] = from.cell;
    const at = (r: number, c: number) => SEGMENTS.find((s) => s.cell[0] === r && s.cell[1] === c)!.key;
    const next: FaceKey | undefined = {
      ArrowLeft: at(row, 0),
      ArrowRight: at(row, 1),
      ArrowUp: at(0, col),
      ArrowDown: at(1, col),
      Home: SEGMENTS[0].key,
      End: SEGMENTS[SEGMENTS.length - 1].key
    }[event.key];
    if (!next) return;
    event.preventDefault();
    select(next);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 440, mx: 'auto', containerType: 'inline-size' }}>
      <Box sx={{ position: 'relative', width: '100%', aspectRatio: '1 / 1' }}>
        <svg width="0" height="0" aria-hidden focusable="false" style={{ position: 'absolute' }}>
          <defs>
            {SEGMENTS.map((s) => (
              <clipPath key={s.key} id={`${clipBase}-${s.key}`} clipPathUnits="objectBoundingBox">
                <path d={outline(s.turns)} transform="scale(0.01)" />
              </clipPath>
            ))}
          </defs>
        </svg>

        <Box role="tablist" aria-label={ariaLabel} aria-describedby={`${id}-flow`} sx={{ position: 'absolute', inset: 0 }}>
          {SEGMENTS.map((s) => (
            <Segment
              key={s.key}
              def={s}
              id={faceTabId(id, s.key)}
              panelId={panelId}
              clipId={`${clipBase}-${s.key}`}
              selected={value === s.key}
              reading={readings[s.key]}
              buttonRef={(el) => {
                tabs.current[s.key] = el;
              }}
              onSelect={() => onChange(s.key)}
              onKeyDown={(e) => onKeyDown(e, s)}
            />
          ))}
        </Box>
        <Box component="span" id={`${id}-flow`} sx={visuallyHidden}>
          The sections form a cycle: Cash, then Financing, then Assets, then Earnings, then back to Cash.
        </Box>

        <Centre title={title} subtitle={subtitle} healthLabel={healthLabel} health={health} />
      </Box>
    </Box>
  );
}

function Segment({
  def,
  id,
  panelId,
  clipId,
  selected,
  reading,
  buttonRef,
  onSelect,
  onKeyDown
}: {
  def: SegmentDef;
  id: string;
  panelId?: string;
  clipId: string;
  selected: boolean;
  reading?: FaceReading;
  buttonRef: (el: HTMLButtonElement | null) => void;
  onSelect: () => void;
  onKeyDown: (event: KeyboardEvent) => void;
}) {
  const d = outline(def.turns);
  const status = reading?.status;

  return (
    // The shadow sits on a wrapper: a filter on the clipped button itself would be clipped too.
    // The wrapper takes no pointer events, so only the button's visible shape is clickable.
    <Box
      sx={(theme: Theme) => ({
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        filter: selected
          ? `drop-shadow(0 8px 18px ${mix(theme.vars.palette.primary[800], 22, 'transparent')})`
          : `drop-shadow(0 2px 6px ${mix(theme.vars.palette.primary[800], 6, 'transparent')})`,
        transition: 'filter 160ms ease',
        zIndex: selected ? 1 : 0
      })}
    >
      <Box
        component="button"
        type="button"
        role="tab"
        id={id}
        ref={buttonRef}
        aria-selected={selected}
        aria-controls={panelId}
        tabIndex={selected ? 0 : -1}
        onClick={onSelect}
        onKeyDown={onKeyDown}
        sx={(theme: Theme) => {
          const { primary, secondary, background } = theme.vars.palette;
          const paper = background.paper;
          const tint = (mode: 'light' | 'dark') =>
            gradient(mix(primary.main, def.tint[mode][0], paper), mix(primary.main, def.tint[mode][1], paper));
          return {
            position: 'absolute',
            inset: 0,
            p: 0,
            m: 0,
            border: 0,
            cursor: 'pointer',
            pointerEvents: 'auto',
            clipPath: `url(#${clipId})`,
            background: selected ? gradient(secondary.light, mix(secondary.main, 22, paper)) : tint('light'),
            color: primary[800],
            font: 'inherit',
            textAlign: 'left',
            outline: 'none',
            transition: 'filter 160ms ease',
            '&:hover': { filter: 'brightness(1.03) saturate(1.08)' },
            '& .face-ring-selected': { opacity: selected ? 1 : 0 },
            '& .face-ring-focus': { opacity: 0 },
            '&:focus-visible .face-ring-focus': { opacity: 1 },
            '--face-icon': selected ? secondary.dark : primary.main,
            '--face-ring': secondary.main,
            '--face-focus': primary.main,
            ...theme.applyStyles('dark', {
              background: selected ? gradient(mix(primary.main, 46, paper), mix(primary.main, 36, paper)) : tint('dark'),
              color: primary.light,
              '--face-icon': selected ? secondary.main : primary[200],
              '--face-focus': '#ffffff'
            })
          };
        }}
      >
        {/* Rings trace the outline from inside: the stroke is centred on the clip edge, so only its
            inner half survives. Focus is a wide translucent band beneath the solid selected ring,
            so the two read differently and both stay visible when they coincide. */}
        <Box
          component="svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        >
          <path
            className="face-ring-focus"
            d={d}
            fill="none"
            stroke="var(--face-focus)"
            strokeOpacity={0.3}
            strokeWidth={12}
            vectorEffect="non-scaling-stroke"
          />
          <path
            className="face-ring-selected"
            d={d}
            fill="none"
            stroke="var(--face-ring)"
            strokeWidth={5}
            vectorEffect="non-scaling-stroke"
          />
        </Box>

        <Box
          component="span"
          sx={{ position: 'absolute', ...def.place, width: '26%', display: 'flex', flexDirection: 'column', gap: '1.2cqi' }}
        >
          <Box component="span" sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box component="span" sx={{ display: 'flex', fontSize: 'clamp(20px, 7cqi, 32px)', color: 'var(--face-icon)' }}>
              {def.icon}
            </Box>
            <Box
              component="span"
              sx={{
                width: 'clamp(9px, 3.2cqi, 14px)',
                height: 'clamp(9px, 3.2cqi, 14px)',
                borderRadius: '50%',
                flex: 'none',
                mt: '0.4cqi',
                ...(status ? { bgcolor: TONE_COLOR[status.tone] } : { border: '1.5px solid currentColor', opacity: 0.45 })
              }}
            />
          </Box>
          <Box component="span" sx={{ fontWeight: 700, fontSize: 'clamp(13px, 4.8cqi, 21px)', lineHeight: 1.15, letterSpacing: '-0.01em' }}>
            {def.label}
          </Box>
          <Sparkline values={reading?.trend} />
          {/* The top row has to clear the disc and the incoming arrow's notch, which leaves room for
              two short lines. Below 360px there is room for none, so the phrase goes to screen
              readers only and the dot carries the reading. The separator keeps the tab's accessible
              name "Cash, Not rated" rather than "CashNot rated". */}
          <Box
            component="span"
            sx={{
              fontSize: 'clamp(11px, 3.3cqi, 15px)',
              lineHeight: 1.25,
              opacity: status ? 0.85 : 0.55,
              minHeight: '2.5em',
              '@container (max-width: 359px)': visuallyHidden
            }}
          >
            <Box component="span" sx={visuallyHidden}>
              ,{' '}
            </Box>
            {status ? status.label : 'Not rated'}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function Sparkline({ values }: { values?: number[] }) {
  // The height is reserved either way, so a segment with no trend lines up with one that has it.
  const box = { display: 'block', width: '100%', height: '5cqi', minHeight: 12 };
  if (!values || values.length < 2) return <Box component="span" sx={box} />;

  const lo = Math.min(...values);
  const span = Math.max(...values) - lo || 1;
  const points = values.map((v, i) => `${(i / (values.length - 1)) * 100},${27 - ((v - lo) / span) * 24}`).join(' ');

  return (
    <Box component="svg" viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden sx={box}>
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </Box>
  );
}

function Centre({ title, subtitle, healthLabel, health }: { title: string; subtitle: string; healthLabel: string; health?: FaceHealth }) {
  const outOf = health?.outOf ?? 10;
  const fraction = health ? Math.min(Math.max(health.score / outOf, 0), 1) : 0;

  return (
    <Box
      sx={(theme: Theme) => ({
        position: 'absolute',
        inset: '35%',
        zIndex: 2,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        boxShadow: `0 0 0 1.2cqi ${mix(theme.vars.palette.background.paper, 70, 'transparent')}, 0 8px 22px ${mix(theme.vars.palette.primary[800], 22, 'transparent')}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.2cqi',
        textAlign: 'center',
        color: 'common.white',
        // Dark mode's primary.main is lightened for text on dark surfaces; white needs the deeper step.
        ...theme.applyStyles('dark', {
          bgcolor: 'primary.dark',
          boxShadow: `0 0 0 1.2cqi ${mix(theme.vars.palette.common.white, 4, 'transparent')}, 0 8px 22px rgba(0,0,0,0.3)`
        })
      })}
    >
      <Box component="span" sx={{ fontSize: 'clamp(18px, 7.6cqi, 34px)', fontWeight: 800, lineHeight: 1, letterSpacing: '-0.01em' }}>
        {title}
      </Box>
      <Box component="span" sx={{ fontSize: 'clamp(10px, 3.4cqi, 15px)', lineHeight: 1.2, opacity: 0.8 }}>
        {subtitle}
      </Box>

      {/* The mock-up's short rule under the subtitle is where the score lives once there is one.
          Without a score it stays a plain rule — a meter at zero would assert a value. */}
      {health ? (
        <>
          <Box
            role="meter"
            aria-label={healthLabel}
            aria-valuemin={0}
            aria-valuemax={outOf}
            aria-valuenow={health.score}
            aria-valuetext={`${health.score.toFixed(1)} out of ${outOf}`}
            sx={{
              width: '42%',
              height: 'clamp(3px, 1.1cqi, 5px)',
              borderRadius: 99,
              bgcolor: 'rgba(255,255,255,0.25)',
              overflow: 'hidden',
              mt: '0.6cqi'
            }}
          >
            <Box sx={{ width: `${fraction * 100}%`, height: '100%', borderRadius: 99, bgcolor: 'secondary.main' }} />
          </Box>
          <Box component="span" aria-hidden sx={{ fontSize: 'clamp(9px, 2.8cqi, 12px)', opacity: 0.75 }}>
            {health.score.toFixed(1)} / {outOf}
          </Box>
        </>
      ) : (
        <Box aria-hidden sx={{ width: '18%', height: 2, borderRadius: 99, bgcolor: 'currentColor', opacity: 0.35, mt: '0.6cqi' }} />
      )}
    </Box>
  );
}
